import { Alert, Button, Flex, useAuthenticator, View } from '@aws-amplify/ui-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { usePasskeyAuth } from '../../hooks/usePasskeyAuth'
import { useProgressiveAuth } from './progressive-auth-context'
import { Divider } from 'antd'
import { KeyOutlined } from '@ant-design/icons'

const isValidEmail = (value: string): boolean => /.+@.+\..+/.test(value)

export function PasskeySignInFooter(): JSX.Element {
  const { toForgotPassword } = useAuthenticator()
  const { availableChallenges, isChallengeReady, setChallenges, resetChallenges } =
    useProgressiveAuth()
  const {
    handlePasskeySignIn,
    status: passkeyStatus,
    isLoading: isPasskeyLoading
  } = usePasskeyAuth()
  const [email, setEmail] = useState('')
  const [discoveryError, setDiscoveryError] = useState<string | null>(null)
  const [isChecking, setIsChecking] = useState(false)
  const lastDiscoveredEmail = useRef<string | null>(null)
  const previousEmail = useRef('')
  const sanitizedEmail = useMemo(() => email.toLowerCase().trim().replace(/\s+/g, ''), [email])
  const passkeyAvailable = isChallengeReady && availableChallenges.includes('WEB_AUTHN')

  useEffect(() => {
    const emailInput = document.querySelector('input[name="username"]') as HTMLInputElement | null

    if (!emailInput) {
      return
    }

    const handleInput = () => {
      setEmail(emailInput.value)
    }

    handleInput()
    emailInput.addEventListener('input', handleInput)
    emailInput.addEventListener('change', handleInput)

    return () => {
      emailInput.removeEventListener('input', handleInput)
      emailInput.removeEventListener('change', handleInput)
    }
  }, [])

  useEffect(() => {
    if (previousEmail.current && previousEmail.current !== sanitizedEmail) {
      lastDiscoveredEmail.current = null
      resetChallenges()
      setDiscoveryError(null)
    }

    previousEmail.current = sanitizedEmail

    if (!sanitizedEmail) {
      resetChallenges()
      lastDiscoveredEmail.current = null
      setDiscoveryError(null)
    }
  }, [resetChallenges, sanitizedEmail])

  const fetchChallenges = useCallback(
    async (emailToCheck: string) => {
      if (!isValidEmail(emailToCheck)) {
        return
      }

      if (lastDiscoveredEmail.current === emailToCheck && isChallengeReady) {
        return
      }

      setIsChecking(true)
      setDiscoveryError(null)

      try {
        const { signIn } = await import('aws-amplify/auth')
        const response = await signIn({
          username: emailToCheck,
          options: {
            authFlowType: 'USER_AUTH' as const
          }
        })

        // @ts-expect-error // it's okay
        const challenges = response.nextStep?.availableChallenges ?? []
        const nextStep = response.nextStep?.signInStep ?? null
        setChallenges(challenges, nextStep)
        lastDiscoveredEmail.current = emailToCheck
      } catch (error) {
        if (error instanceof Error) {
          console.error('Failed to discover sign-in challenges', error)
          setDiscoveryError(error?.message ?? 'Could not check passkey availability')
          lastDiscoveredEmail.current = null
          resetChallenges()
        }
      } finally {
        setIsChecking(false)
      }
    },
    [isChallengeReady, resetChallenges, setChallenges]
  )

  useEffect(() => {
    if (!isValidEmail(sanitizedEmail)) {
      return
    }

    const timeout = window.setTimeout(() => {
      void fetchChallenges(sanitizedEmail)
    }, 600)

    return () => window.clearTimeout(timeout)
  }, [fetchChallenges, sanitizedEmail])

  const handlePasskeyClick = useCallback(() => {
    if (!sanitizedEmail || !isValidEmail(sanitizedEmail)) {
      setDiscoveryError('Enter a valid email before using a passkey')
      return
    }

    void handlePasskeySignIn(sanitizedEmail)
  }, [handlePasskeySignIn, sanitizedEmail])

  return (
    <View>
      <Divider className={'my-1'} />

      {passkeyAvailable && (
        <Button
          width='100%'
          variation='primary'
          onClick={handlePasskeyClick}
          isLoading={isPasskeyLoading}
          loadingText='Signing in...'
          marginBottom='0.5rem'
          gap='0.3rem'
          disabled={isChecking}
        >
          <KeyOutlined />
          Passkey sign-in
        </Button>
      )}

      {passkeyStatus && ['warning', 'error'].includes(passkeyStatus.type) && (
        <Alert variation={passkeyStatus.type} marginBottom='0.5rem'>
          {passkeyStatus.message}
        </Alert>
      )}

      {discoveryError && (
        <Alert variation='error' marginBottom='0.5rem'>
          {discoveryError}
        </Alert>
      )}

      <Flex justifyContent='center'>
        <Button variation='link' onClick={toForgotPassword} size='small'>
          Forgot your password?
        </Button>
      </Flex>
    </View>
  )
}
